/**
 * runDream() — the dreamer orchestration.
 *
 * Composes the seams: propose → applyDeltas gates →
 * commitWithRebase → commitCursorAdvance. The CRITICAL fix
 * lives here: the cursor advance is committed ONLY after a confirmed brain write.
 * Every terminal failure (proposal failure, applyDeltas `aborted`, write
 * `deferred`/`repair`) is FAIL-CLOSED — no advance, a `failure` digest entry, and
 * the cost is always preserved.
 */
import type { NormalizedDreamInput, AdvancePlan } from "../types";
import { commitCursorAdvance } from "../cursor";
import { commitWithRebase, verifyEndState } from "./rebase";
import type { IndexEdit } from "./rebase";
import { assembleDigest } from "./digest";
import { commitDreamDigest } from "./digest-commit";
import { defaultDreamSeams } from "./seams";                  // real propose+apply composition (2-arg edge)
import { capCorpus, DEFAULT_CORPUS_CAP } from "./corpus-cap"; // hard per-cycle input bound
import type {
  DreamDeps, DreamResult, DigestEntry, ProposalDelta, FileChange,
} from "./types";

const MAX_WRITE_ATTEMPTS = 5;

/** Injected seams — defaulted to the real impls at the edge; faked in unit tests. */
export interface RunDreamSeams {
  propose: (input: NormalizedDreamInput, deps: DreamDeps) => Promise<{
    deltas: ProposalDelta[] | null;
    cost: { model: string; inputTokens: number; outputTokens: number; ms: number };
    failure?: string;
  }>;
  applyDeltas: (
    deltas: ProposalDelta[], input: NormalizedDreamInput, deps: DreamDeps,
  ) => Promise<{ changes: FileChange[]; indexEdit?: IndexEdit; digest: DigestEntry[]; aborted: boolean; abortReason?: string }>;
}

export async function runDream(
  input: NormalizedDreamInput & { advance: AdvancePlan },
  deps: DreamDeps,
  // Defaults to the real propose+apply composition (2-arg edge contract:
  // `runDream(input, deps)`); tests still inject explicit fake seams.
  seams: RunDreamSeams = defaultDreamSeams(),
): Promise<DreamResult> {
  const digest: DigestEntry[] = [];

  // 0. Hard per-cycle input bound. A never-dreamed worker /
  // --reset / post-downtime backlog can harvest the whole window; cap to the
  // N-OLDEST so we propose over one context AND advance the cursor only over the
  // KEPT rows (capCorpus re-derives the partial advance). The NEWEST remainder is
  // deferred — recorded as a capture-gap (honest proxy framing) so it
  // is visible, and re-harvested next cycle (the cursor never advanced past it).
  const cap = deps.corpusCap ?? DEFAULT_CORPUS_CAP;
  const { capped, deferred } = capCorpus(input, cap);
  if (deferred.length > 0) {
    deps.logger.info({ at: "runDream", worker: input.worker, corpusCap: cap, kept: capped.conversations.length, deferred: deferred.length });
    digest.push({
      kind: "capture-gap",
      detail: `corpus cap ${String(cap)}: dreamed the ${String(capped.conversations.length)} oldest of ${String(input.conversations.length)} harvested; ${String(deferred.length)} newer deferred to a later cycle (cursor advanced only over the dreamed rows)`,
      evidence: deferred.map((c) => c.conversationId).filter((id): id is string => typeof id === "string"),
    });
  }
  // From here on, `input` is the CAPPED view: propose/apply see only the kept
  // conversations (so deferred conv_ids are never valid evidence) and the advance
  // covers only the kept rows.
  input = capped;

  if (input.conversations.length === 0) {
    await commitCursorAdvance(input.advance, deps.cursor);
    deps.logger.info({
      at: "runDream",
      worker: input.worker,
      terminal: "no-corpus",
      skipped: input.stats.skipped,
      advanced: true,
      skipLedger: input.skipLedger.map((s) => `${s.reason}:${String(s.count)}`),
    });
    return { worker: input.worker, applied: [], digest, advanced: true };
  }

  // 1. propose (model call + retry + parse/validate live inside seams.propose)
  const proposed = await seams.propose(input, deps);
  digest.push({ kind: "cost", model: proposed.cost.model, inputTokens: proposed.cost.inputTokens, outputTokens: proposed.cost.outputTokens, ms: proposed.cost.ms });

  if (!proposed.deltas) {
    // model/parse terminal disposition: fail closed — no writes, no advance
    deps.logger.warn({ at: "runDream", worker: input.worker, terminal: "model-failure", detail: proposed.failure });
    digest.push({ kind: "failure", detail: proposed.failure ?? "model failure", evidence: [] });
    return { worker: input.worker, applied: [], digest, advanced: false };
  }

  // 2. apply gates (evidence-membership, origin-tier, idempotency, verb→FileChange)
  const applied = await seams.applyDeltas(proposed.deltas, input, deps);
  digest.push(...applied.digest);

  if (applied.aborted) {
    // origin-operator supersede aborts the whole batch — never half-apply
    deps.logger.warn({ at: "runDream", worker: input.worker, terminal: "apply-aborted", detail: applied.abortReason });
    digest.push({ kind: "failure", detail: applied.abortReason ?? "apply aborted", evidence: [] });
    return { worker: input.worker, applied: [], digest, advanced: false };
  }

  if (applied.changes.length === 0) {
    // nothing to write (all no-op/flag/rejected) — advance is safe (window genuinely consumed)
    await commitCursorAdvance(input.advance, deps.cursor);
    await emitDigest(input, deps, digest);
    return { worker: input.worker, applied: [], digest, advanced: true };
  }

  // 3. atomic write with rebase-rebuild + end-state verify
  const message = applied.digest.length ? `dream: ${input.worker} consolidation` : `dream: ${input.worker}`;
  const result = await commitWithRebase({
    writer: deps.brain,
    message,
    changes: applied.changes,
    ...(applied.indexEdit ? { indexEdit: applied.indexEdit } : {}),
    maxAttempts: MAX_WRITE_ATTEMPTS,
    verify: async () => verifyEndState(deps.brain, applied.changes),
  });

  if (result.outcome !== "committed") {
    // rebase-exhaust / repair / error: fail closed — write NOT confirmed → DO NOT advance
    deps.logger.error({ at: "runDream", worker: input.worker, terminal: result.outcome, attempts: result.attempts });
    digest.push({ kind: "failure", detail: `write ${result.outcome} after ${String(result.attempts)} attempt(s)`, evidence: [] });
    return { worker: input.worker, applied: [], digest, advanced: false };
  }

  // 4. THE FIX — commit the cursor advance ONLY after a confirmed write
  await commitCursorAdvance(input.advance, deps.cursor);
  deps.logger.info({ at: "runDream", worker: input.worker, committed: result.newSha, advanced: true });

  // 5. digest as its OWN commit AFTER the delta batch + the advance.
  await emitDigest(input, deps, digest);

  const appliedChanges = applied.digest.filter((e): e is Extract<DigestEntry, { kind: "changed" }> => e.kind === "changed");
  return { worker: input.worker, applied: appliedChanges, digest, advanced: true };
}

/**
 * Assemble the inline DigestEntry[] and commit it as its OWN commit,
 * keyed by the cursor-window upper bound (`input.window.to`). Best-effort / reconstructible:
 * the deltas + advance have ALREADY landed, so a digest write/verify failure must NEVER fail
 * the whole dream — it is surfaced as a `failure` digest entry (carrying the reason), never
 * silently lost. Mutates `digest` in place to record the failure.
 */
async function emitDigest(
  input: NormalizedDreamInput & { advance: AdvancePlan },
  deps: DreamDeps,
  digest: DigestEntry[],
): Promise<void> {
  const cycle = input.window.to; // window upper bound — the locked <cycle> key
  // fold the harvest skip-ledger INTO the committed digest — one `skip-ledger`
  // entry per SkipEntry. The internally-accumulated `digest` only knows what propose/apply did;
  // the skip-ledger lives on `input` (computed by the pipeline before propose). Pushing it here
  // covers EVERY terminal path that emits a digest (main success + empty-changes both funnel
  // through emitDigest) without divergent copies, and mutates `digest` in place so the returned
  // DreamResult.digest carries it too. Mirrors the CLI's stdout render of input.skipLedger.
  for (const s of input.skipLedger) {
    digest.push({ kind: "skip-ledger", reason: s.reason, count: s.count, sampleIds: s.sampleIds });
  }
  // The deltas + advance have ALREADY landed (index.ts:90/66), so this leg is best-effort:
  // a thrown error (fetchTip/readFile/token/network — see writer.ts/digest-commit.ts) OR a
  // {committed:false} return both collapse to the SAME non-fatal `failure` entry. Wrapping the
  // whole body (assembleDigest included) guarantees the post-advance digest leg NEVER rejects.
  try {
    const assembled = assembleDigest({ worker: input.worker, cycle, entries: digest });
    const res = await commitDreamDigest(assembled, deps.brain, { fetchTip: () => deps.brain.fetchTip() });
    if (!res.committed) {
      // reconstructible: deltas + advance are intact; the digest is re-emittable next cycle.
      deps.logger.warn({ at: "runDream", worker: input.worker, digestCommit: res.reason ?? "error" });
      digest.push({ kind: "failure", detail: `digest commit ${res.reason ?? "error"} (reconstructible; deltas + advance intact)`, evidence: [] });
    }
  } catch (e) {
    // Truly best-effort: deltas + advance are intact; surface the throw as a non-fatal entry, never lose it.
    const detail = e instanceof Error ? e.message : String(e);
    deps.logger.warn({ at: "runDream", worker: input.worker, digestCommit: "error", detail });
    digest.push({ kind: "failure", detail: `digest commit error: ${detail} (reconstructible; deltas + advance intact)`, evidence: [] });
  }
}
